<script lang="ts">
  import { getContext } from 'svelte';
  import { fade } from 'svelte/transition';
  import { BulletPoint } from '../../components/index';
  import {
    formatPhoneNumber,
    isValidEmailAddress,
    isValidPhoneNumber,
  } from 'core-components/helpers';
  import { formatPhoneNumberHref } from '../../helpers/phoneNumberHelper';
  import type { Hero3, Text } from '../types';
  import type { MktSettings } from 'customer-components/lib/types';

  type DynamicStep = {
    type: 'checkbox' | 'radio-button';
    title: Text;
    options: { text: string; checked?: boolean }[];
    group: string | null;
    error: boolean;
    button: Text;
  };

  type StepsMap = (
    | DynamicStep
    | {
        type: 'submit' | 'confirm';
        heading: Text;
        button: Text;
        message?: Text;
        response?: Text;
      }
  )[];

  const TIME_OUT = 2000;
  const hash = getContext<string>('hash');
  const isEditable = getContext<string>('isEditable');

  export let properties: Hero3,
    steps: Hero3['items'],
    phoneNumber: MktSettings['phoneNumber'],
    step = 1;

  let dynamicSteps = initialDynamicSteps(steps);

  $: ({ form, thankYou, submit } = properties);

  // Submit Step
  let name = '',
    email = '',
    phone = '',
    message = '',
    errorNameLength = false,
    errorEmailLength = false,
    errorEmailValid = false,
    errorMessageLength = false,
    errorPhoneLength = false,
    errorPhoneValid = false;

  function stepNext() {
    if (step < numberMaxOfSteps) {
      step += 1;
    } else {
      step = 1;
    }
  }

  function next() {
    if (stepActive.type === 'checkbox') {
      if (stepActive.options.map(option => option.checked).every(el => !el)) {
        stepActive.error = true;
        return;
      } else {
        stepNext();
      }
    }

    if (stepActive.type === 'radio-button') {
      if (!stepActive.group) {
        stepActive.error = true;
        return;
      } else {
        stepNext();
      }
    }

    if (stepActive.type === 'confirm') {
      resetForm();
      stepNext();
    }
  }

  function initialDynamicSteps(steps) {
    return steps.map(el => ({
      ...el,
      options: el.options
        ? el.options.map(op => {
            if (el.type === 'checkbox') {
              return {
                text: op,
                checked: false,
              };
            } else if (el.type === 'radio-button') {
              return {
                text: op,
              };
            }
          })
        : [],
      error: false,
      group: el.type === 'radio-button' ? '' : null,
    }));
  }

  function resetForm() {
    // we need to reset the whole form
    step = 1;
    dynamicSteps = initialDynamicSteps(steps);
    name = '';
    email = '';
    phone = '';
    message = '';
  }

  function resetErrors() {
    setTimeout(() => {
      dynamicSteps = dynamicSteps.map(i => ({
        ...i,
        error: false,
      }));
    }, 100);
    errorNameLength = false;
    errorEmailLength = false;
    errorEmailValid = false;
    errorPhoneLength = false;
    errorPhoneValid = false;
    errorMessageLength = false;
  }

  function handlePhoneChange() {
    phone = formatPhoneNumber(phone);
  }

  function transformString(inputString: string) {
    // Remove all special characters except for spaces
    const cleanedString = inputString.replace(/[^a-zA-Z0-9\s]/g, '');
    // Convert to lowercase
    const lowercasedString = cleanedString.toLowerCase();
    // Replace spaces with "-"
    const finalString = lowercasedString.replace(/\s+/g, '-');

    return finalString;
  }

  function handleSubmit(event: Event) {
    const additionalMessage = event.srcElement[4].value;
    let dynamicQuestions = [];
    dynamicSteps.forEach(el => {
      if (el.type === 'checkbox') {
        const question = el.title.value;
        const answers = el.options.filter(i => i.checked).map(i => i.text);
        dynamicQuestions.push({
          questionKey: transformString(question),
          question: question,
          answers,
        });
      } else if (el.type === 'radio-button') {
        const question = el.title.value;
        const answers = [el.group];
        dynamicQuestions.push({
          questionKey: transformString(question),
          question: question,
          answers,
        });
      }
    });

    const newInput = document.createElement('input');
    newInput.type = 'hidden';
    newInput.name = 'questions';
    newInput.value = JSON.stringify(dynamicQuestions);

    event.srcElement[event.srcElement.length - 2].value = additionalMessage;

    event.srcElement.appendChild(newInput);

    if (name.length === 0) {
      errorNameLength = true;
    }
    if (email.length === 0 && form?.emailRequired) {
      errorEmailLength = true;
    } else if (!isValidEmailAddress(email)) {
      errorEmailValid = true;
    }

    if (phone?.length === 0 && form?.phoneRequired) {
      errorPhoneLength = true;
    } else if (!isValidPhoneNumber(phone)) {
      errorPhoneValid = true;
    }

    if (message.trim().length === 0) {
      errorMessageLength = true;
    }

    if (
      errorNameLength ||
      errorEmailLength ||
      errorEmailValid ||
      errorPhoneLength ||
      errorPhoneValid ||
      errorMessageLength
    ) {
      return;
    }

    properties.submitLead(event);
    stepNext();
  }

  $: {
    if (isEditable) {
      dynamicSteps = initialDynamicSteps(steps);
    }
  }

  let stepsMap: StepsMap;
  $: stepsMap = [
    ...dynamicSteps,
    {
      type: 'submit',
      heading: submit.heading,
      button: submit.button,
    },
    {
      type: 'confirm',
      heading: thankYou.heading,
      message: thankYou.message,
      button: thankYou.button,
      response: thankYou.response,
    },
  ];
  $: numberMaxOfSteps = stepsMap.length;
  $: stepActive = stepsMap[step - 1];
</script>

<svelte:window on:keypress={resetErrors} />

<div class="instant-quote">
  <div
    id="form"
    class="hero-form-holder"
    style="border-color:var(--color-accent, #8dc63f);"
  >
    <div class="backdrop" />
    <h3 class="form-title" id="form1">
      {@html form.title.value}
    </h3>
    <form class="hero-form" on:submit|preventDefault={handleSubmit}>
      <h3 class="form-title-mobile">{@html form.title.value}</h3>
      <input type="hidden" name="hash" value={hash} />
      {#if stepActive?.type === 'checkbox'}
        <div
          class="hero-form-content hero-form-1"
          in:fade={{ duration: TIME_OUT }}
        >
          <div class="hero-form-top">
            <h4><span class="number">1</span> {stepActive.title.value}</h4>
            {#if stepActive.error}
              <p class="error-msg">
                You must select at least one option below.
              </p>
            {/if}
          </div>

          <div class="options-holder">
            {#each stepActive.options as stepAct}
              <div class="option-block">
                <label class="custom-checkbox">
                  <input
                    name="checkbox-input"
                    type="checkbox"
                    bind:checked={stepAct.checked}
                    on:click={resetErrors}
                  />
                  <div class="checkbox-box" />
                  <span class="input-text-span text-small">{stepAct.text}</span>
                  <span class="checkbox-border" />
                </label>
              </div>
            {/each}
          </div>
          <div class="bottom-boxes">
            <button
              type="button"
              class="primary-button submit-choice-1 nextHeroForm"
              on:click={next}>{stepActive.button.value}</button
            >
            <div class="small-text-box">
              {#each form.callouts.value as text}
                <div>
                  <BulletPoint bulletIcon={form.callouts.icon} />
                  <p class="text-small">
                    {@html text}
                  </p>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {:else if stepActive?.type === 'radio-button'}
        <div
          class="hero-form-content hero-form-2"
          in:fade={{ duration: TIME_OUT }}
        >
          <div class="hero-form-top">
            <h4><span class="number">2</span>{stepActive.title.value}</h4>
            {#if stepActive.error}
              <p class="error-msg">You must select an option below</p>
            {/if}
          </div>
          <div class="options-holder">
            {#each stepActive.options as stepAct}
              <div class="option-block">
                <label class="custom-radio">
                  <input
                    name="radio-input"
                    type="radio"
                    value={stepAct.text}
                    bind:group={stepActive.group}
                    on:click={() => {
                      resetErrors();
                      setTimeout(() => {
                        if (stepAct.text) next();
                      }, 400);
                    }}
                  />
                  <div class="radio-box" />
                  <span class="input-text-span text-small">{stepAct.text}</span>
                  <span class="radio-border" />
                </label>
              </div>
            {/each}
          </div>
          <div class="bottom-boxes">
            <button
              type="button"
              class="primary-button nextHeroForm"
              on:click={next}>{stepActive.button.value}</button
            >
            {#if dynamicSteps.length > 1}
              <div class="form-progress-bar">
                <div class="progress-step progress-step--completed">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                  >
                    <path
                      d="M13.3333 4.33337L5.99996 11.6667L2.66663 8.33337"
                      stroke="#262641"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div class="bullet-line" />
                <div class="progress-step progress-step--next">
                  <p>2</p>
                </div>
                <div class="bullet-line" />
                <div class="progress-step">
                  <p>3</p>
                </div>
              </div>
            {/if}
            <div class="small-text-box">
              {#each form.callouts.value as text}
                <div>
                  <BulletPoint bulletIcon={form.callouts.icon} />
                  <p class="text-small">
                    {@html text}
                  </p>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {:else if stepActive?.type === 'submit'}
        <div
          class="hero-form-content hero-form-3"
          in:fade={{ duration: TIME_OUT }}
        >
          <div class="hero-form-top">
            <h4>
              <span class="number submit">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 3L4.5 8.5L2 6"
                    stroke="white"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>{@html submit.heading.value}
            </h4>
          </div>
          <div class="options-holder options-inputs">
            <div class="input-holder">
              <div class="input-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M7.5 6.5C7.5 8.981 9.519 11 12 11C14.481 11 16.5 8.981 16.5 6.5C16.5 4.019 14.481 2 12 2C9.519 2 7.5 4.019 7.5 6.5ZM20 21H21V20C21 16.141 17.859 13 14 13H10C6.14 13 3 16.141 3 20V21H4H5H19H20Z"
                    fill="#111111"
                    fill-opacity="0.64"
                  />
                </svg>
                <input
                  id="fullname"
                  class="text verify-fullname"
                  placeholder="Full Name"
                  name="fullName"
                  type="text"
                  bind:value={name}
                />
              </div>
              {#if errorNameLength}
                <p class="error-msg fullname-error">
                  This field is missing. Provide full name.
                </p>
              {/if}
            </div>
            <div class="input-holder">
              <div class="input-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20 4H4C2.896 4 2 4.896 2 6V18C2 19.104 2.896 20 4 20H20C21.104 20 22 19.104 22 18V6C22 4.896 21.104 4 20 4ZM20 8.7L12 14.034L4 8.7V6.297L12 11.63L20 6.297V8.7Z"
                    fill="#111111"
                    fill-opacity="0.64"
                  />
                </svg>
                <input
                  id="email"
                  class="text verify-email"
                  placeholder="Email"
                  name="email"
                  type="email"
                  bind:value={email}
                />
                {#if !form?.emailRequired}
                  <span class="input-side-text">Optional</span>
                {/if}
              </div>
              {#if errorEmailLength}
                <p class="error-msg">Please enter a valid email.</p>
              {/if}
              {#if errorEmailValid}
                <p class="error-check-valid">Please enter a valid email.</p>
              {/if}
            </div>

            <div class="input-holder">
              <div class="input-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M8.38028 8.85335C9.07627 10.303 10.0251 11.6616 11.2266 12.8632C12.4282 14.0648 13.7869 15.0136 15.2365 15.7096C15.3612 15.7694 15.4235 15.7994 15.5024 15.8224C15.7828 15.9041 16.127 15.8454 16.3644 15.6754C16.4313 15.6275 16.4884 15.5704 16.6027 15.4561C16.9523 15.1064 17.1271 14.9316 17.3029 14.8174C17.9658 14.3864 18.8204 14.3864 19.4833 14.8174C19.6591 14.9316 19.8339 15.1064 20.1835 15.4561L20.3783 15.6509C20.9098 16.1824 21.1755 16.4481 21.3198 16.7335C21.6069 17.301 21.6069 17.9713 21.3198 18.5389C21.1755 18.8242 20.9098 19.09 20.3783 19.6214L20.2207 19.779C19.6911 20.3087 19.4263 20.5735 19.0662 20.7757C18.6667 21.0001 18.0462 21.1615 17.588 21.1601C17.1751 21.1589 16.8928 21.0788 16.3284 20.9186C13.295 20.0576 10.4326 18.4332 8.04466 16.0452C5.65668 13.6572 4.03221 10.7948 3.17124 7.76144C3.01103 7.19699 2.93092 6.91477 2.9297 6.50182C2.92833 6.0436 3.08969 5.42311 3.31411 5.0236C3.51636 4.66357 3.78117 4.39876 4.3108 3.86913L4.46843 3.7115C4.99987 3.18006 5.2656 2.91433 5.55098 2.76999C6.11854 2.48292 6.7888 2.48292 7.35636 2.76999C7.64174 2.91433 7.90747 3.18006 8.43891 3.7115L8.63378 3.90637C8.98338 4.25597 9.15819 4.43078 9.27247 4.60655C9.70347 5.26944 9.70347 6.12403 9.27247 6.78692C9.15819 6.96269 8.98338 7.1375 8.63378 7.4871C8.51947 7.60142 8.46231 7.65857 8.41447 7.72538C8.24446 7.96281 8.18576 8.30707 8.26748 8.58743C8.29048 8.66632 8.32041 8.72866 8.38028 8.85335Z"
                    fill="#111111"
                    fill-opacity="0.64"
                  />
                </svg>
                <input
                  class="text phone-js"
                  placeholder="Phone Number"
                  id="phone"
                  name="phone"
                  type="tel"
                  bind:value={phone}
                  on:input={handlePhoneChange}
                />
                {#if !form?.phoneRequired}
                  <span class="input-side-text">Optional</span>
                {/if}
              </div>
              {#if errorPhoneLength}
                <p class="error-msg">Please enter a valid phone number.</p>
              {/if}
              {#if errorPhoneValid}
                <p class="error-check-valid">
                  Please enter a valid phone number.
                </p>
              {/if}
            </div>
            <div class="input-holder">
              <div class="custom-textarea">
                <textarea
                  id="hero-text-area"
                  class="text"
                  name="comments"
                  placeholder=" "
                  bind:value={message}
                />

                <label
                  class="text"
                  for="hero-text-area"
                  data-svelte-h="svelte-v1kyob"
                  >{form?.additionalDetails || 'Additional Details'}</label
                >
              </div>
              {#if errorMessageLength}
                <p class="error-msg">
                  Please provide us some details so we can provide you with a
                  quote
                </p>
              {/if}
            </div>
          </div>
          <div class="bottom-boxes">
            <button
              id="email_catcher_form_submit"
              type="submit"
              class="primary-button nextHeroForm next-thank-you"
              >{@html submit.button.value}</button
            >
            {#if dynamicSteps.length > 1}
              <div class="form-progress-bar">
                <div class="progress-step progress-step--completed">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                  >
                    <path
                      d="M13.3333 4.33337L5.99996 11.6667L2.66663 8.33337"
                      stroke="#262641"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div class="bullet-line" />
                <div class="progress-step progress-step--completed">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                  >
                    <path
                      d="M13.3333 4.33337L5.99996 11.6667L2.66663 8.33337"
                      stroke="#262641"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div class="bullet-line" />
                <div class="progress-step progress-step--next">
                  <p>3</p>
                </div>
              </div>
            {/if}
            <div class="small-text-box">
              {#each form.callouts.value as text}
                <div>
                  <BulletPoint bulletIcon={form.callouts.icon} />
                  <p class="text-small">
                    {@html text}
                  </p>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {:else if stepActive?.type === 'confirm'}
        <div
          class="hero-form-content hero-form--thanks hero-form-4"
          in:fade={{ duration: TIME_OUT }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="60"
            height="61"
            viewBox="0 0 60 61"
            fill="none"
          >
            <circle cx="30" cy="30.5" r="25" fill="white" />
            <path
              d="M30 5.5C16.215 5.5 5 16.715 5 30.5C5 44.285 16.215 55.5 30 55.5C43.785 55.5 55 44.285 55 30.5C55 16.715 43.785 5.5 30 5.5ZM25.0025 41.5325L15.72 32.27L19.25 28.73L24.9975 34.4675L38.2325 21.2325L41.7675 24.7675L25.0025 41.5325Z"
              fill="#8DC63F"
            />
          </svg>

          <h3>{@html stepActive.heading.value}</h3>

          <p>
            {@html stepActive.message.value}
          </p>

          <button
            class="primary-button get-another-quote"
            on:click={() => resetForm()}>{@html stepActive.button.value}</button
          >

          <p>or</p>
          <a href="tel:{formatPhoneNumberHref(phoneNumber?.value)}">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path
                d="M15.2559 10.7442C15.1786 10.6667 15.0867 10.6053 14.9857 10.5633C14.8846 10.5214 14.7762 10.4998 14.6667 10.4998C14.5573 10.4998 14.4489 10.5214 14.3478 10.5633C14.2467 10.6053 14.1549 10.6667 14.0776 10.7442L12.7492 12.0725C12.1334 11.8892 10.9842 11.4725 10.2559 10.7442C9.52757 10.0158 9.11091 8.86667 8.92757 8.25084L10.2559 6.9225C10.3334 6.84518 10.3948 6.75335 10.4367 6.65225C10.4787 6.55115 10.5002 6.44278 10.5002 6.33334C10.5002 6.22389 10.4787 6.11552 10.4367 6.01443C10.3948 5.91333 10.3334 5.82149 10.2559 5.74417L6.92257 2.41084C6.84525 2.33338 6.75342 2.27194 6.65232 2.23001C6.55122 2.18808 6.44285 2.1665 6.33341 2.1665C6.22396 2.1665 6.11559 2.18808 6.0145 2.23001C5.9134 2.27194 5.82156 2.33338 5.74424 2.41084L3.48424 4.67084C3.16757 4.9875 2.98924 5.4225 2.99591 5.86667C3.01507 7.05334 3.32924 11.175 6.57758 14.4233C9.82591 17.6717 13.9476 17.985 15.1351 18.005H15.1584C15.5984 18.005 16.0142 17.8317 16.3292 17.5167L18.5892 15.2567C18.6667 15.1793 18.7281 15.0875 18.7701 14.9864C18.812 14.8853 18.8336 14.7769 18.8336 14.6675C18.8336 14.5581 18.812 14.4497 18.7701 14.3486C18.7281 14.2475 18.6667 14.1557 18.5892 14.0783L15.2559 10.7442ZM15.1501 16.3375C14.1101 16.32 10.5517 16.0408 7.75591 13.2442C4.95091 10.4392 4.67924 6.86834 4.66257 5.84917L6.33341 4.17834L8.48841 6.33334L7.41091 7.41084C7.31296 7.50871 7.24094 7.62945 7.20137 7.76214C7.16179 7.89484 7.15591 8.0353 7.18424 8.17084C7.20424 8.26667 7.69341 10.5392 9.07674 11.9225C10.4601 13.3058 12.7326 13.795 12.8284 13.815C12.9639 13.8441 13.1045 13.8387 13.2373 13.7993C13.3701 13.7598 13.4908 13.6875 13.5884 13.5892L14.6667 12.5117L16.8217 14.6667L15.1501 16.3375Z"
                fill="white"
              />
            </svg>
            Call Us {@html phoneNumber?.value}
          </a>
          <div class="bottom-boxes">
            <div class="small-text-box">
              <div>
                <svg
                  width="16"
                  height="12"
                  viewBox="0 0 16 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.07904 8.94848L1.52576 5.3952L0 6.92096L5.07904 12L15.5533 1.52576L14.0275 0L5.07904 8.94848Z"
                    fill="currentColor"
                  />
                </svg>
                <p class="text-small">{@html stepActive.response.value}</p>
              </div>
            </div>
          </div>
        </div>
      {/if}
    </form>
  </div>
</div>
